import './JobSummaryCard.scss';

import { IonCard, IonCardContent } from '@ionic/react';
import { isNil } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { GenericMessages } from 'src/intl/GenericMessages';
import type { JobInfoJobLog } from 'src/pages/job-info/types';
import { selectIsMetric } from 'src/redux/profile-preferences/selectors';
import { formatMinutesToHoursMinutes } from 'src/utils/helpers/chartHelpers';
import { TimeTable, calcLanceSpeed, msToTime } from 'src/utils/logs/logHelpers';
import type { JobLogV2Data } from 'src/utils/logs/types';
import UpdateJobDetailsModal from '../job-details-update/UpdateJobDetailsModal';
import { JobSummary } from './JobSummary';
import type { JobLog } from 'src/models/jobLog';
import { canSeeShares, usePolicy } from '../../../models/policies';

interface JobSummaryCardProps {
  info: JobInfoJobLog | undefined;
  data: JobLogV2Data;
  description: string | undefined;
  siteName: string | undefined;
  heatExchangerModelNumber: string | undefined;
  showTimeDetails: boolean;
  showEstimatedTimeToCompletion: boolean;
  showUpdateJobButton: boolean;
  onReportSharedWithClicked?: () => void;
  jobStatus: 'complete' | 'in-progress' | 'live';
  timeRangeMin: number;
  timeRangeMax: number;
  timezone: string;
  activeRunTime: number;
}

export const JobSummaryCard: React.FC<JobSummaryCardProps> = props => {
  const { showUpdateJobButton } = props;
  const { formatNumber, formatMessage, locale } = useIntl();
  const [editJobDetailsModalOpen, setEditJobDetailsModalOpen] = useState<boolean>(false);
  const isMetric = useSelector(selectIsMetric);
  const canSeeReportSharedWith = usePolicy(canSeeShares, props.info);

  const calcTimeInTubes = useCallback(
    (timeMs: number, tubesCleaned: number | undefined): string | undefined => {
      if (!timeMs || !tubesCleaned) return;
      return msToTime(timeMs / tubesCleaned, formatNumber);
    },
    [formatNumber],
  );

  const lastTubeCount = props.data.tubeCount[props.data.tubeCount.length - 1];
  const cleanedCount = lastTubeCount?.cleaned ?? 0;
  const totalCount = lastTubeCount ? lastTubeCount.total : 0;

  const lastTimeEvent = useMemo(() => {
    if (!props.data.cleanRate) return undefined;
    const lastIndex = props.data.cleanRate.length - 1;
    if (lastIndex < 0) return undefined;
    return props.data.cleanRate[lastIndex];
  }, [props.data.cleanRate]);

  const lanceSpeed = useMemo(
    () => calcLanceSpeed(props.data.tubeSheet, isMetric, props.timeRangeMin, props.timeRangeMax),
    [isMetric, props.data.tubeSheet, props.timeRangeMax, props.timeRangeMin],
  );

  const estimatedTimeToCompletion = useMemo(() => {
    if (isNil(lastTimeEvent?.EstCompletionMinutes) || isNaN(lastTimeEvent!.EstCompletionMinutes))
      return formatMessage(GenericMessages.unknownPlaceholder);
    else return formatMinutesToHoursMinutes(lastTimeEvent!.EstCompletionMinutes);
  }, [formatMessage, lastTimeEvent]);

  const onUpdateJobClick = useMemo(() => {
    if (!showUpdateJobButton) return undefined;
    return () => {
      setEditJobDetailsModalOpen(true);
    };
  }, [showUpdateJobButton]);

  const filename = props.info?.originalFileName || (props.info as JobLog)?.name || undefined;

  const startTime = useMemo(() => {
    const data = props.data;
    if (!data.timeTable.length) return undefined;

    const timeTable = TimeTable.from(data.timeTable);
    const startTimeEpoch = timeTable.getInstant(0);
    const formattedStartTime = new Intl.DateTimeFormat(locale, {
      timeZone: props.timezone,
      dateStyle: 'short',
      timeStyle: 'long',
    }).format(startTimeEpoch);

    return formattedStartTime;
  }, [props.data, locale, props.timezone]);

  return (
    <IonCard className="job-summary-card">
      <IonCardContent className="job-summary-card-content">
        <JobSummary
          fileName={filename}
          tubesCleaned={cleanedCount.toLocaleString()}
          totalTubes={totalCount.toLocaleString()}
          timeInTube={calcTimeInTubes(props.activeRunTime, cleanedCount) || undefined}
          activeRunTime={msToTime(props.activeRunTime, formatNumber) || undefined}
          controller={props.info?.physicalSerial}
          siteName={props.siteName}
          modelNumber={props.heatExchangerModelNumber}
          description={props.description}
          lanceSpeed={lanceSpeed}
          updatedAt={props.info?.updatedAt}
          createdAt={props.info?.createdAt}
          timezone={props.timezone}
          permissions={props.info?.permissions}
          emailGuestShares={props.info?.emailGuestShares}
          estimatedTimeToCompletion={estimatedTimeToCompletion}
          showTimeDetails={props.showTimeDetails}
          showEstimatedTimeToCompletion={props.showEstimatedTimeToCompletion}
          showUpdateJobButton={showUpdateJobButton}
          updateJobDetails={onUpdateJobClick}
          startTime={startTime}
          onReportSharedWithClicked={props.onReportSharedWithClicked}
          canSeeReportSharedWith={canSeeReportSharedWith}
        />
      </IonCardContent>
      {props.info && (
        <UpdateJobDetailsModal
          jobId={props.info._id}
          orgId={props.info.org}
          originalFileName={filename || ''}
          isOpen={editJobDetailsModalOpen}
          onClose={() => setEditJobDetailsModalOpen(false)}
          tagId={props.info?.logTagIds?.[0] ?? ''}
          existingJobDetailValues={{
            description: props.description ?? '',
            heatExchangerModelNumber: props.heatExchangerModelNumber ?? '',
            siteName: props.siteName ?? '',
            timezone: props.timezone ?? 'UTC',
          }}
        />
      )}
    </IonCard>
  );
};
